/**
 * NuveRise — Wishlist (client-side, localStorage-backed)
 *
 * Storage: localStorage key 'nuveWishlist' — array of:
 *   { handle, title, image, price, url, addedAt }
 *
 * DOM contract:
 *   [data-wishlist-toggle][data-product-handle][data-product-*]
 *     - Buttons inside product cards. Click toggles the product in the list.
 *     - aria-pressed reflects current state; updated on every change.
 *   [data-wishlist-count]
 *     - Badge element(s); textContent set to count, hidden when count === 0.
 *
 * Events:
 *   'wishlist:change' — dispatched on document with { detail: items[] } after every change.
 *     The wishlist page listens to re-render its grid.
 *
 * Public API (window.NuveWishlist): get, add(item), remove(handle), toggle(item),
 *   has(handle), clear().
 */
(function () {
  'use strict';

  var KEY = 'nuveWishlist';

  function get() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function set(items) {
    try {
      localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      // Quota or disabled storage — swallow silently; UI stays in last good state.
    }
    syncDom();
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: items }));
  }

  function has(handle) {
    if (!handle) return false;
    return get().some(function (i) { return i.handle === handle; });
  }

  function add(item) {
    if (!item || !item.handle) return;
    if (has(item.handle)) return;
    var items = get();
    items.unshift({
      handle: item.handle,
      title: item.title || '',
      image: item.image || '',
      price: item.price || '',
      url: item.url || '#',
      addedAt: Date.now()
    });
    set(items);
  }

  function remove(handle) {
    if (!handle) return;
    set(get().filter(function (i) { return i.handle !== handle; }));
  }

  function toggle(item) {
    if (!item || !item.handle) return;
    if (has(item.handle)) {
      remove(item.handle);
    } else {
      add(item);
    }
  }

  function clear() {
    set([]);
  }

  // --- DOM sync ---

  function syncDom() {
    var items = get();
    var count = items.length;

    // Badges
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = String(count);
      if (count === 0) {
        el.setAttribute('hidden', '');
      } else {
        el.removeAttribute('hidden');
      }
    });

    // Buttons — reflect pressed state for the matching product handle
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-product-handle');
      var pressed = handle && has(handle);
      btn.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    });
  }

  // --- Delegated click ---

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var item = {
      handle: btn.getAttribute('data-product-handle'),
      title: btn.getAttribute('data-product-title') || '',
      image: btn.getAttribute('data-product-image') || '',
      price: btn.getAttribute('data-product-price') || '',
      url: btn.getAttribute('data-product-url') || '#'
    };
    toggle(item);

    // Micro-animation — pop the heart. Honors reduced-motion via media query
    // (we still call animate, but the runtime is cheap and visually subtle).
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduce && btn.animate) {
      try {
        btn.animate(
          [
            { transform: 'scale(1)' },
            { transform: 'scale(1.22)' },
            { transform: 'scale(1)' }
          ],
          { duration: 240, easing: 'ease-out' }
        );
      } catch (_) {}
    }
  });

  // --- Init ---

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncDom);
  } else {
    syncDom();
  }

  // Re-sync when the tab regains focus (another tab may have changed the list).
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) syncDom();
  });

  // Public API
  window.NuveWishlist = {
    get: get,
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    clear: clear
  };
})();
